/**
 * Token management hook for authentication.
 * Handles token verification, refresh, and initialization.
 */

import { useCallback } from 'react'
import { ENV } from '@/config/env'
import type { TokenManager } from '@/services/token-manager'
import type { ApiResponseEnvelope } from '@/services/api'
import { TARGET_LANG_CODES } from '@/constants/languages'
import {
    type User,
    type AuthStatus,
    type ApiErrorResponse,
    type ApiUserResponse,
    type ApiAuthResponse,
    AuthError,
    unwrapApiResponse,
    normalizeUserData,
    extractTranslationKey,
} from './types'
import { trackAuthRehydrated, trackAuthRefreshFailed } from '@/services/analytics.service'
import type { TokenClearReason } from '@/services/cross-tab-coordinator'
import { browserScheduler } from '@/lib/scheduler'
import { useServiceContext } from '../ServiceContext'
import { captureIntendedRoute, clearIntendedRoute } from '@/features/auth/utils/routePreservation'

const API_BASE_URL = ENV.API_BASE_URL

/**
 * Check if an error is transient (network error, 5xx, timeout, JSON parsing) vs auth error (401/403).
 */
function isTransientError(error: Error | AuthError): boolean {
    if (error instanceof AuthError) {
        // Auth errors (401, 403) are not transient
        if (error.status === 401 || error.status === 403) {
            return false
        }
        // 5xx errors are transient
        if (error.status && error.status >= 500) {
            return true
        }
    }
    // Network errors, timeouts, JSON parsing errors, etc. are transient
    if (
        error instanceof TypeError ||
        error instanceof SyntaxError ||
        error.message.includes('fetch') ||
        error.message.includes('JSON') ||
        error.message.includes('network')
    ) {
        return true
    }
    return false
}

/**
 * Check if tokens are actually invalid (expired or malformed).
 * Returns true only if we can confirm tokens are invalid.
 *
 * Logic:
 * - If refresh token exists and is expired → tokens are invalid
 * - If refresh token exists and is not expired → tokens are valid (can refresh)
 * - If only access token exists and is expired → tokens are invalid (no way to refresh)
 * - If only access token exists and is not expired → tokens are valid
 */
export function areTokensInvalid(
    tokenManager: TokenManager,
    accessToken: string | null,
    refreshToken: string | null
): boolean {
    // If we have a refresh token, check if it's expired
    if (refreshToken) {
        try {
            if (tokenManager.isTokenExpired(refreshToken)) {
                // Refresh token is expired → tokens are invalid
                return true
            }
            // Refresh token is valid → tokens are not invalid (we can refresh)
            return false
        } catch {
            // If we can't parse the refresh token, consider it invalid
            return true
        }
    }

    // No refresh token, check access token
    if (accessToken) {
        try {
            if (tokenManager.isTokenExpired(accessToken)) {
                // Access token expired and no refresh token → tokens are invalid
                return true
            }
            // Access token is valid → tokens are not invalid
            return false
        } catch {
            // If we can't parse the access token, consider it invalid
            return true
        }
    }

    // If we have no tokens at all, they're not invalid (just missing)
    return false
}

/**
 * Safely parse JSON from a response, handling errors gracefully.
 */
async function safeJsonParse<T>(response: Response): Promise<T> {
    try {
        return (await response.json()) as T
    } catch (error) {
        // If JSON parsing fails, wrap it in a more descriptive error
        if (error instanceof SyntaxError) {
            throw new Error(`Invalid JSON response: ${error.message}`, { cause: error })
        }
        throw error
    }
}

/**
 * Fetch user data from /api/v1/auth/me endpoint.
 */
async function fetchUserData(accessToken: string): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
        },
    })

    if (!response.ok) {
        const errorData = (await safeJsonParse<ApiErrorResponse>(response).catch(
            () => ({})
        )) as ApiErrorResponse
        throw new AuthError(
            errorData.message || 'Failed to get user info',
            extractTranslationKey(errorData) || 'errors.server.internal',
            response.status,
            errorData.error || errorData.code
        )
    }

    const responseData = await safeJsonParse<ApiResponseEnvelope<ApiUserResponse>>(response)
    const userData = unwrapApiResponse(responseData)

    return normalizeUserData(userData, TARGET_LANG_CODES)
}

interface UseTokenManagementParams {
    tokenManager: TokenManager
    setUser: React.Dispatch<React.SetStateAction<User | null>>
    setStatus: React.Dispatch<React.SetStateAction<AuthStatus>>
    setLoading: React.Dispatch<React.SetStateAction<boolean>>
    setHydrated: React.Dispatch<React.SetStateAction<boolean>>
    clearAuthData: (
        reason?: TokenClearReason,
        errorDetails?: { errorType?: string; status?: number; message?: string }
    ) => void
    /** Optional callback to show notifications (translation key, severity) */
    showNotification?: (
        translationKey: string,
        severity?: 'success' | 'error' | 'warning' | 'info'
    ) => void
}

interface UseTokenManagementReturn {
    verifyToken: (token: string) => Promise<void>
    refreshToken: (refreshToken: string) => Promise<void>
    initializeAuth: (location?: {
        pathname: string
        search?: string
        hash?: string
    }) => Promise<void>
}

/**
 * Hook for token verification, refresh, and initialization.
 */
export const useTokenManagement = ({
    tokenManager,
    setUser,
    setStatus,
    setLoading,
    setHydrated,
    clearAuthData,
    showNotification,
}: UseTokenManagementParams): UseTokenManagementReturn => {
    const serviceContext = useServiceContext()
    const scheduler = serviceContext?.scheduler ?? browserScheduler
    const verifyToken = useCallback(
        async (token: string) => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                })

                if (!response.ok) {
                    const responseData = (await safeJsonParse<ApiErrorResponse>(response).catch(
                        () => ({})
                    )) as ApiErrorResponse
                    throw new AuthError(
                        responseData.message || 'Token verification failed',
                        extractTranslationKey(responseData) ||
                            'errors.auth.tokenVerificationFailed',
                        response.status,
                        responseData.error || responseData.code
                    )
                }

                const responseData =
                    await safeJsonParse<ApiResponseEnvelope<ApiUserResponse>>(response)
                const userData = unwrapApiResponse(responseData)

                const normalizedUserData = normalizeUserData(userData, TARGET_LANG_CODES)

                setUser(normalizedUserData)
                setStatus('signedIn')
            } catch (error) {
                console.error('Token verification error:', error)
                // Only clear tokens if it's a confirmed auth error (401/403)
                // For transient errors, preserve tokens so user can retry
                const isTransient = isTransientError(error as Error | AuthError)
                if (
                    !isTransient &&
                    error instanceof AuthError &&
                    (error.status === 401 || error.status === 403)
                ) {
                    // Confirmed auth error - tokens are invalid
                    clearAuthData('auth_error', {
                        errorType: 'AuthError',
                        status: error.status,
                        message: error.message,
                    })
                    setStatus('signedOut')
                } else {
                    // Transient error - preserve tokens, just set status
                    setStatus('signedOut')
                }
                setLoading(false)
            }
        },
        [clearAuthData, setUser, setStatus, setLoading]
    )

    const refreshToken = useCallback(
        async (refreshTokenValue: string) => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ refresh_token: refreshTokenValue }),
                })

                if (!response.ok) {
                    const responseData = (await safeJsonParse<ApiErrorResponse>(response).catch(
                        () => ({})
                    )) as ApiErrorResponse
                    throw new AuthError(
                        responseData.message || 'Token refresh failed',
                        extractTranslationKey(responseData) || 'errors.auth.tokenRefreshFailed',
                        response.status,
                        responseData.error || responseData.code
                    )
                }

                // Parse and unwrap the response envelope
                const refreshResponseData =
                    await safeJsonParse<ApiResponseEnvelope<ApiAuthResponse>>(response)
                const data = unwrapApiResponse(refreshResponseData)

                // Validate access_token before proceeding
                if (!data.access_token || data.access_token.trim() === '') {
                    throw new AuthError(
                        'Refresh response missing access_token',
                        'errors.auth.refreshMissingToken',
                        500
                    )
                }

                // Store new tokens
                if (data.access_token) {
                    tokenManager.setAccessToken(data.access_token)
                }
                if (data.refresh_token) {
                    tokenManager.setRefreshToken(data.refresh_token)
                }

                // Get user info with new token
                const userResponse = await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
                    headers: {
                        Authorization: `Bearer ${data.access_token}`,
                        'Content-Type': 'application/json',
                    },
                })

                if (!userResponse.ok) {
                    const userErrorData = (await safeJsonParse<ApiErrorResponse>(
                        userResponse
                    ).catch(() => ({}))) as ApiErrorResponse
                    throw new AuthError(
                        userErrorData.message || 'Failed to get user info after refresh',
                        extractTranslationKey(userErrorData) || 'errors.auth.userInfoFailed',
                        userResponse.status,
                        userErrorData.error || userErrorData.code
                    )
                }

                const userResponseData =
                    await safeJsonParse<ApiResponseEnvelope<ApiUserResponse>>(userResponse)
                const userData = unwrapApiResponse(userResponseData)

                const normalizedUserData = normalizeUserData(userData, TARGET_LANG_CODES)

                setUser(normalizedUserData)
                tokenManager.setUserInStorage(normalizedUserData)
                setStatus('signedIn')
            } catch (error) {
                console.error('Token refresh error:', error)
                // Only clear tokens if it's a confirmed auth error (401/403)
                // For transient errors, preserve tokens so user can retry
                const isTransient = isTransientError(error as Error | AuthError)
                if (
                    !isTransient &&
                    error instanceof AuthError &&
                    (error.status === 401 || error.status === 403)
                ) {
                    // Confirmed auth error - tokens are invalid
                    clearAuthData('auth_error', {
                        errorType: 'AuthError',
                        status: error.status,
                        message: error.message,
                    })
                    setStatus('signedOut')
                } else {
                    // Transient error - preserve tokens, just set status
                    setStatus('signedOut')
                }
                setLoading(false)
            }
        },
        [tokenManager, clearAuthData, setUser, setStatus, setLoading]
    )

    const initializeAuth = useCallback(
        async (location?: { pathname: string; search?: string; hash?: string }) => {
            const storedUser = tokenManager.getUserFromStorage()
            const storedAccessToken = tokenManager.getAccessToken()
            const storedRefreshToken = tokenManager.getRefreshToken()

            const hadAccessToken = !!storedAccessToken
            const hadUserData = !!storedUser

            // Track if we've successfully hydrated (to avoid setting hydrated with signedOut status)
            let hasHydrated = false

            // Check for refresh token first (even if access token/user missing)
            if (storedRefreshToken) {
                // Capture intended route before setting status to 'restoring'
                if (location) {
                    captureIntendedRoute(location)
                }
                setStatus('restoring')
                setLoading(true)
                showNotification?.('auth:rehydrating', 'info')

                let retryAttempted = false
                let refreshSucceeded = false

                // Attempt refresh with retry logic
                const attemptRefresh = async (): Promise<void> => {
                    let refreshData: ApiAuthResponse

                    // Step 1: Refresh API call
                    try {
                        const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({ refresh_token: storedRefreshToken }),
                        })

                        if (!response.ok) {
                            const responseData = (await safeJsonParse<ApiErrorResponse>(
                                response
                            ).catch(() => ({}))) as ApiErrorResponse
                            throw new AuthError(
                                responseData.message || 'Token refresh failed',
                                extractTranslationKey(responseData),
                                response.status,
                                responseData.error || responseData.code
                            )
                        }

                        // Parse and unwrap the response envelope
                        const responseData =
                            await safeJsonParse<ApiResponseEnvelope<ApiAuthResponse>>(response)
                        refreshData = unwrapApiResponse(responseData)

                        // Validate access_token before proceeding
                        if (!refreshData.access_token || refreshData.access_token.trim() === '') {
                            throw new AuthError(
                                'Refresh response missing access_token',
                                'errors.auth.refreshMissingToken',
                                500
                            )
                        }

                        // Store new tokens
                        if (refreshData.access_token) {
                            tokenManager.setAccessToken(refreshData.access_token)
                        }
                        if (refreshData.refresh_token) {
                            tokenManager.setRefreshToken(refreshData.refresh_token)
                        }
                    } catch (error) {
                        // This is a refresh API failure
                        const isTransient = isTransientError(error as Error | AuthError)

                        // Retry once on transient errors
                        if (isTransient && !retryAttempted) {
                            retryAttempted = true
                            showNotification?.('auth:rehydrationRetrying', 'info')
                            // Wait 500ms before retry
                            await new Promise<void>((resolve) => {
                                scheduler.setTimeout(() => resolve(), 500)
                            })
                            return attemptRefresh()
                        }

                        // If retry failed or it's an auth error, handle failure
                        if (isTransient) {
                            // Transient error after retry - don't set status yet, will try access token fallback
                            console.error('Token refresh failed after retry:', error)
                            trackAuthRefreshFailed('retry_failed', {
                                retry_attempted: true,
                                error_type: error instanceof AuthError ? 'AuthError' : 'Error',
                            })
                            showNotification?.('auth:rehydrationFailed', 'error')
                            // Keep status as 'restoring' to allow fallback attempt
                            setStatus('restoring')
                            setLoading(false)
                            // Don't clear tokens on transient error - user can try again
                        } else {
                            // Check if this is a confirmed auth error (401/403) and tokens are actually invalid
                            const isAuthError =
                                error instanceof AuthError &&
                                (error.status === 401 || error.status === 403)
                            const tokensInvalid = areTokensInvalid(
                                tokenManager,
                                storedAccessToken,
                                storedRefreshToken
                            )

                            if (isAuthError && tokensInvalid) {
                                // Confirmed auth error with invalid tokens - clear tokens and logout
                                console.error(
                                    'Token refresh failed (auth error, tokens invalid):',
                                    error
                                )
                                trackAuthRefreshFailed('auth_error', {
                                    retry_attempted: retryAttempted,
                                    error_type: 'AuthError',
                                    status: error.status,
                                    tokens_invalid: true,
                                })
                                showNotification?.('auth:rehydrationFailed', 'error')
                                // Clear preserved route on auth failure
                                clearIntendedRoute()
                                clearAuthData('auth_error', {
                                    errorType: 'AuthError',
                                    status: error.status,
                                    message: error.message,
                                })
                                setStatus('signedOut')
                                setLoading(false)
                            } else {
                                // Not a confirmed auth error or tokens might still be valid - preserve tokens
                                console.error('Token refresh failed (preserving tokens):', error)
                                trackAuthRefreshFailed('preserved_tokens', {
                                    retry_attempted: retryAttempted,
                                    error_type: error instanceof AuthError ? 'AuthError' : 'Error',
                                    status: error instanceof AuthError ? error.status : undefined,
                                    is_auth_error: isAuthError,
                                    tokens_invalid: tokensInvalid,
                                })
                                showNotification?.('auth:rehydrationFailed', 'error')
                                // Keep status as 'restoring' to allow fallback attempt
                                setStatus('restoring')
                                setLoading(false)
                                // Don't clear tokens - might be a temporary issue
                                // Return early to prevent fetchUserData from running
                                return
                            }
                        }
                        // Return early to prevent fetchUserData from running
                        return
                    }

                    // Step 2: Fetch user data (only if refresh API succeeded)
                    try {
                        // Fetch user data (always fetch to ensure we have latest state)
                        // refreshData is guaranteed to be non-null here and access_token is validated above
                        const userData = await fetchUserData(refreshData.access_token)

                        // Update user state
                        setUser(userData)
                        tokenManager.setUserInStorage(userData)
                        setStatus('signedIn')
                        setLoading(false)

                        // Emit telemetry
                        trackAuthRehydrated({
                            had_access_token: hadAccessToken,
                            had_user_data: hadUserData,
                            rebuilt_user_state: !hadUserData || !storedAccessToken,
                        })

                        // Show success notification
                        showNotification?.('auth:rehydrated', 'success')

                        refreshSucceeded = true
                    } catch (error) {
                        // This is a fetchUserData failure (refresh API succeeded)
                        const isTransient = isTransientError(error as Error | AuthError)
                        const isAuthError =
                            error instanceof AuthError &&
                            (error.status === 401 || error.status === 403)

                        console.error('Failed to fetch user data after successful refresh:', error)
                        trackAuthRefreshFailed('user_data_fetch_failed', {
                            refresh_succeeded: true,
                            retry_attempted: retryAttempted,
                            error_type: error instanceof AuthError ? 'AuthError' : 'Error',
                            status: error instanceof AuthError ? error.status : undefined,
                            is_transient: isTransient,
                            is_auth_error: isAuthError,
                        })

                        // If it's a transient error, we might want to retry fetchUserData
                        // For now, we'll preserve tokens and set status to restoring
                        // The user can try again or we can use stored user data if available
                        if (storedUser && !isAuthError) {
                            // Use stored user data as fallback for transient errors
                            setUser(storedUser)
                            setStatus('signedIn')
                            setLoading(false)
                            showNotification?.('auth:rehydrated', 'success')
                            refreshSucceeded = true
                        } else {
                            // Auth error or no stored user - set status to restoring to allow retry
                            setStatus('restoring')
                            setLoading(false)
                            showNotification?.('auth:rehydrationFailed', 'error')
                        }
                    }
                }

                await attemptRefresh()

                // If refresh succeeded, we're done
                if (refreshSucceeded) {
                    setHydrated(true)
                    return
                }

                // If refresh failed but we have an access token, try verifying it as fallback
                if (storedAccessToken) {
                    try {
                        setStatus('restoring')
                        await verifyToken(storedAccessToken)
                        // Successfully verified access token - set hydrated and return
                        setHydrated(true)
                        hasHydrated = true
                        setLoading(false)
                        return
                    } catch (error) {
                        console.error('Error during access token verification (fallback):', error)
                        // Only clear tokens if it's a confirmed auth error (401/403) and token is invalid
                        const isTransient = isTransientError(error as Error | AuthError)
                        const isAuthError =
                            error instanceof AuthError &&
                            (error.status === 401 || error.status === 403)
                        const tokenInvalid = areTokensInvalid(tokenManager, storedAccessToken, null)

                        if (!isTransient && isAuthError && tokenInvalid) {
                            // Confirmed auth error with invalid token - clear tokens
                            // Clear preserved route on auth failure
                            clearIntendedRoute()
                            clearAuthData('auth_error', {
                                errorType: 'AuthError',
                                status: error.status,
                                message: error.message,
                            })
                            // Only set hydrated after clearing tokens (clearAuthData handles state)
                            setHydrated(true)
                            hasHydrated = true
                        } else {
                            // Transient error or token might still be valid - preserve tokens
                            // Set status to 'signedOut' - tokens are preserved for next attempt
                            setStatus('signedOut')
                        }
                        setLoading(false)
                    }
                } else {
                    // Refresh failed and no access token to fall back to
                    // Set status to 'signedOut' since we can't try anything else
                    setStatus('signedOut')
                }
            } else if (storedAccessToken) {
                // No refresh token, but access token exists - verify it
                try {
                    setStatus('restoring')
                    await verifyToken(storedAccessToken)
                    // Explicitly set hydrated after success
                    setHydrated(true)
                    hasHydrated = true
                    setLoading(false)
                } catch (error) {
                    console.error('Error during token verification:', error)
                    // Only clear tokens if it's a confirmed auth error (401/403) and token is invalid
                    const isTransient = isTransientError(error as Error | AuthError)
                    const isAuthError =
                        error instanceof AuthError && (error.status === 401 || error.status === 403)
                    const tokenInvalid = areTokensInvalid(tokenManager, storedAccessToken, null)

                    if (!isTransient && isAuthError && tokenInvalid) {
                        // Confirmed auth error with invalid token - clear tokens
                        // Clear preserved route on auth failure
                        clearIntendedRoute()
                        clearAuthData('auth_error', {
                            errorType: 'AuthError',
                            status: error.status,
                            message: error.message,
                        })
                        // Only set hydrated after clearing tokens (clearAuthData handles state)
                        setHydrated(true)
                        hasHydrated = true
                    } else {
                        // Transient error or token might still be valid - preserve tokens
                        // Set status to 'signedOut' - tokens are preserved for next attempt
                        setStatus('signedOut')
                    }
                    setLoading(false)
                }
            }

            // Only set hydrated if we haven't already set it
            // This ensures we always set hydrated, even if verification failed
            if (!hasHydrated) {
                setHydrated(true)
                // If no tokens exist, status should already be 'signedOut' from initial state
                // No need to set it again
            }
        },
        [
            tokenManager,
            verifyToken,
            clearAuthData,
            setUser,
            setStatus,
            setLoading,
            scheduler,
            setHydrated,
            showNotification,
        ]
    )

    return {
        verifyToken,
        refreshToken,
        initializeAuth,
    }
}
